export const ASSISTANT_ACTION_EVENT = 'warbox:assistant-action';
const STORAGE_KEY = 'warbox.assistant.actions';
const RETENTION_DAYS = 7;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
export const ACTION_RETENTION_MS = RETENTION_DAYS * MS_PER_DAY;

export const ASSISTANT_ACTION_STATUS = Object.freeze({
    PREPARING: 'preparing',
    COMPLETED: 'completed',
    ERROR: 'error',
} as const);

const UNKNOWN_ERROR = 'unknown';

export interface TAssistantActionState {
    requestId: string;
    updatedAt: string;
    leadId?: string;
    intent?: string;
    status?: string;
    title?: string;
    startedAt?: string;
    completedAt?: string;
    error?: string | null;
    [field: string]: unknown;
}

type TAssistantActions = Record<string, TAssistantActionState>;

const isRecent = (action: TAssistantActionState, now: number) =>
    now - new Date(action.updatedAt).getTime() <= ACTION_RETENTION_MS;

/**
 * Drops actions not updated within the retention window.
 */
export const purgeAssistantActions = (
    actions: TAssistantActions,
    now: number = Date.now()
): TAssistantActions =>
    Object.fromEntries(
        Object.entries(actions).filter(([, action]) => isRecent(action, now))
    );

const readActions = (): TAssistantActions => {
    if (typeof window === 'undefined') return {};
    try {
        return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');
    } catch (_) {
        return {};
    }
};

export const getAssistantActions = (): TAssistantActions => readActions();

export const updateAssistantAction = (
    requestId: string,
    changes: Partial<TAssistantActionState>
) => {
    if (!requestId || typeof window === 'undefined') return;
    const actions = purgeAssistantActions(readActions());
    actions[requestId] = {
        ...actions[requestId],
        ...changes,
        requestId,
        updatedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(actions));
    window.dispatchEvent(
        new CustomEvent(ASSISTANT_ACTION_EVENT, {
            detail: actions[requestId],
        })
    );
};

export const startAssistantAction = (
    recommendation: { requestId: string; leadId?: string; title?: string },
    intent: string
) =>
    updateAssistantAction(recommendation.requestId, {
        leadId: recommendation.leadId,
        intent,
        status: ASSISTANT_ACTION_STATUS.PREPARING,
        title: recommendation.title,
        startedAt: new Date().toISOString(),
        error: null,
    });

export const completeAssistantAction = (requestId: string) =>
    updateAssistantAction(requestId, {
        status: ASSISTANT_ACTION_STATUS.COMPLETED,
        completedAt: new Date().toISOString(),
        error: null,
    });

export const failAssistantAction = (requestId: string, error?: string) =>
    updateAssistantAction(requestId, {
        status: ASSISTANT_ACTION_STATUS.ERROR,
        error: error || UNKNOWN_ERROR,
    });
